'use client';

import { useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import PreGameMenuButton from '@/app/components/shared/PreGameMenuButton';
import { segmentClient } from '@/lib/analytics/segment';
import { generateRoomId } from '@/lib/client-utils';
import { useResumableGame } from '@/app/hooks/useResumableGame';
import { ResumeGameModal } from '@/app/components/shared/new-ui/ResumeGameModal';

export default function Page() {
  const router = useRouter();
  const [customRoomCode, setCustomRoomCode] = useState('');
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [hasShownModal, setHasShownModal] = useState(false);

  // Check for any resumable game
  const { resumableGame, isLoading: isResumableLoading } = useResumableGame(null, true);

  useEffect(() => {
    segmentClient.track('Screen Displayed', {
      displayChoices: ['New Game', 'Join Room'],
      eventCategory: 'game setup',
      eventSubCategory: 'select-room',
    });
  }, []);

  // Auto-show resume modal when a resumable game is found
  useEffect(() => {
    if (!isResumableLoading && resumableGame && !hasShownModal) {
      setShowResumeModal(true);
      setHasShownModal(true);
    }
  }, [isResumableLoading, resumableGame, hasShownModal]);

  const newGame = () => {
    router.push('/quests');
    segmentClient.track('Button Pressed', {
      choiceValue: 'New Game',
      displayChoices: ['New Game', 'Join Room'],
      eventCategory: 'game setup',
      eventSubCategory: 'select-room',
    });
  };

  const joinRoom = () => {
    const trimmedCode = customRoomCode.trim();
    if (!trimmedCode) return;
    router.push(`/rooms/${trimmedCode}`);
    segmentClient.track('Button Pressed', {
      choiceValue: 'Join Room',
      displayChoices: ['New Game', 'Join Room'],
      eventCategory: 'game setup',
      eventSubCategory: 'select-room',
    });
  };

  const handleResumeGame = () => {
    if (!resumableGame || !resumableGame.quest_id) return;

    const roomId = generateRoomId();
    const params = new URLSearchParams({ quest: resumableGame.quest_id });
    params.set('resume_game_id', resumableGame.game_id);

    // Don't hide modal - let navigation handle the transition
    router.push(`/rooms/${roomId}?${params.toString()}`);
  };

  const handleStartNewGame = () => {
    // Close modal and go to quest selection
    setShowResumeModal(false);
    router.push('/quests');
  };

  const isJoinDisabled = customRoomCode.trim().length === 0;

  return (
    <main className="flex flex-col gap-10 items-center justify-center h-full">
      {/* Hamburger Menu */}
      <PreGameMenuButton />

      {/* Content overlay (video + dark overlay come from layout) */}
      <div className="relative z-20 flex flex-col items-center justify-center h-full gap-8 text-center max-w-2xl mx-auto px-4">
        {/* Main Content */}
        <div className="flex flex-col items-center gap-12">
          {/* Main Title */}
          <Image
            src="/game_ui/wits-end-logo-light.png"
            alt="Wit's End"
            width={559}
            height={130}
            className="w-[559px] max-w-[85%] drop-shadow-2xl"
          />

          {/* Main action buttons */}
          <div className="flex flex-col gap-3 items-center">
            <button
              className="rounded-[86px] bg-gradient-to-b from-[rgba(255,191,96,0.25)] to-[rgba(255,152,0,0.61)] backdrop-blur-md w-80 max-w-[80%] h-[60px] text-white text-[22px] font-bold transition-all duration-200 hover:scale-105 hover:from-[rgba(255,191,96,0.35)] hover:to-[rgba(255,152,0,0.71)] active:scale-95 drop-shadow-lg"
              onClick={newGame}
            >
              New Game
            </button>

            {/* Loading state while checking for resumable game */}
            {isResumableLoading && (
              <div className="flex items-center gap-2 text-white/50 text-sm h-[40px]">
                <div className="w-4 h-4 border-2 border-white/30 border-t-amber-400 rounded-full animate-spin" />
                <span>Checking for saved progress...</span>
              </div>
            )}

            {/* Resume Game button - shows when resumable game exists */}
            {!isResumableLoading && resumableGame && (
              <button
                className="rounded-[86px] bg-[rgba(255,191,96,0.10)] border-2 border-white/5 backdrop-blur-sm w-80 max-w-[80%] h-[60px] text-white text-[22px] font-bold transition-all duration-200 hover:scale-105 hover:bg-[rgba(255,191,96,0.20)] active:scale-95"
                onClick={() => setShowResumeModal(true)}
              >
                Resume Game
              </button>
            )}
          </div>

          <div className="flex flex-col gap-2 items-center relative w-full">
            <p className="text-white/50 text-center text-lg">Have a room code?</p>
            <input
              type="text"
              placeholder="Room code"
              className="w-80 h-[60px] max-w-[80%] rounded-[86px] bg-[rgba(40,40,40,0.70)] border-2 border-white/5 text-center text-white/50 backdrop-blur-sm text-[22px] font-bold"
              value={customRoomCode}
              onChange={(e) => setCustomRoomCode(e.target.value)}
            />
            {!isJoinDisabled && (
              <button
                className="w-80 max-w-[80%] h-[60px] rounded-[86px] font-bold text-[22px] bg-[rgba(255,191,96,0.10)] border-2 border-white/5 text-center text-white backdrop-blur-sm hover:bg-[rgba(255,191,96,0.20)] active:scale-95"
                onClick={joinRoom}
              >
                Join room
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Auto-popup Resume Game Modal */}
      {resumableGame && (
        <ResumeGameModal
          isOpen={showResumeModal}
          onClose={() => setShowResumeModal(false)}
          onResume={handleResumeGame}
          onStartNew={handleStartNewGame}
          resumableGame={resumableGame}
        />
      )}
    </main>
  );
}
